import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { DataService } from "./data";
import { SearchService } from "./search.service";
import { Icon } from "./icon";

const QUICK_DESTINATIONS = ["Kandy", "Sigiriya", "Ella", "Galle", "Colombo"];

@Component({
  selector: "app-search-overlay",
  standalone: true,
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (search.isOpen()) {
      <div
        class="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-5"
      >
        <button
          type="button"
          aria-label="Close search"
          (click)="search.close()"
          class="absolute inset-0 bg-ink/80 backdrop-blur-sm"
        ></button>

        <div
          class="relative w-full max-w-2xl glass rounded-[1.75rem] overflow-hidden shadow-2xl"
        >
          <div
            class="flex items-center gap-4 px-6 sm:px-8 py-6 border-b border-sand/60"
          >
            <app-icon name="search" [size]="20" class="text-ink/40 shrink-0" />
            <input
              #input
              type="text"
              [value]="query()"
              (input)="query.set(input.value)"
              placeholder="Search destinations, tours, the whole island…"
              class="flex-1 bg-transparent text-lg sm:text-xl font-serif focus:outline-none placeholder:text-ink/30 min-w-0"
              aria-label="Search"
            />
            <kbd
              class="hidden sm:inline-block text-[10px] font-mono text-ink/40 border border-sand rounded px-1.5 py-0.5"
              >esc</kbd
            >
          </div>

          <div class="max-h-[55vh] overflow-y-auto">
            @if (!query().trim()) {
              <div class="px-6 sm:px-8 py-6">
                <div
                  class="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40 mb-3"
                >
                  Popular destinations
                </div>
                <div class="flex flex-wrap gap-2">
                  @for (d of quickDestinations; track d) {
                    <button
                      type="button"
                      (click)="query.set(d)"
                      class="px-4 py-2 rounded-full text-sm font-medium bg-paper-soft hover:bg-saffron/30 transition-colors"
                    >
                      {{ d }}
                    </button>
                  }
                </div>
              </div>
            }

            @if (results().length) {
              <ul class="border-t border-sand/60">
                @for (tour of results(); track tour.id) {
                  <li>
                    <button
                      type="button"
                      (click)="go(['/tours', tour.id])"
                      class="w-full flex items-center gap-4 px-6 sm:px-8 py-4 hover:bg-paper-soft transition-colors text-left group"
                    >
                      <img
                        [src]="tour.image"
                        alt=""
                        class="w-14 h-14 object-cover rounded-xl shrink-0"
                        referrerpolicy="no-referrer"
                      />
                      <span class="flex-1 min-w-0">
                        <span
                          class="block font-serif text-lg leading-tight truncate"
                          >{{ tour.title }}</span
                        >
                        <span class="block text-xs text-ink/50 mt-0.5"
                          >{{ tour.location }} · {{ tour.duration }}</span
                        >
                      </span>
                      <span class="text-right shrink-0">
                        <span
                          class="block text-[9px] uppercase tracking-widest text-ink/35 font-sans font-bold"
                          >From</span
                        >
                        <span class="route-code text-sm text-vermilion"
                          >&#36;{{ tour.price }}</span
                        >
                      </span>
                      <app-icon
                        name="arrow-right"
                        [size]="16"
                        class="text-ink/30 group-hover:text-vermilion group-hover:translate-x-0.5 transition-all shrink-0"
                      />
                    </button>
                  </li>
                }
              </ul>
            } @else if (query().trim()) {
              <div class="px-6 sm:px-8 py-10 text-center text-ink/50">
                No tours match "{{ query() }}" yet - try a region like Ella or
                Galle, or
                <button
                  type="button"
                  (click)="goContact()"
                  class="text-vermilion font-semibold underline underline-offset-2"
                >
                  tell us what you're after</button
                >.
              </div>
            }

            <div
              class="border-t border-sand/60 px-6 sm:px-8 py-4 flex flex-wrap gap-x-6 gap-y-2"
            >
              <button
                type="button"
                (click)="go(['/tours'])"
                class="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-vermilion transition-colors"
              >
                All Tours
              </button>
              <button
                type="button"
                (click)="go(['/about'])"
                class="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-vermilion transition-colors"
              >
                Our Story
              </button>
              <button
                type="button"
                (click)="go(['/attractions'])"
                class="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-vermilion transition-colors"
              >
                Attractions
              </button>
              <button
                type="button"
                (click)="go(['/ayurveda'])"
                class="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-vermilion transition-colors"
              >
                Wellness
              </button>
              <button
                type="button"
                (click)="go(['/gallery'])"
                class="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-vermilion transition-colors"
              >
                Gallery
              </button>
              <button
                type="button"
                (click)="goContact()"
                class="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-vermilion transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class SearchOverlay {
  private platformId = inject(PLATFORM_ID);
  private data = inject(DataService);
  private router = inject(Router);
  search = inject(SearchService);

  private inputRef = viewChild<ElementRef<HTMLInputElement>>("input");

  quickDestinations = QUICK_DESTINATIONS;
  query = signal("");

  results = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return [];
    return this.data
      .getTours()()
      .filter((t) =>
        [t.title, t.location, t.region, t.province].some((f) =>
          f.toLowerCase().includes(q),
        ),
      )
      .slice(0, 6);
  });

  constructor() {
    effect(() => {
      if (this.search.isOpen()) {
        this.query.set("");
        if (isPlatformBrowser(this.platformId)) {
          if (document.body) document.body.style.overflow = "hidden";
          queueMicrotask(() => this.inputRef()?.nativeElement.focus());
        }
      } else if (isPlatformBrowser(this.platformId) && document.body) {
        document.body.style.overflow = "";
      }
    });
  }

  @HostListener("document:keydown", ["$event"])
  onKeydown(e: KeyboardEvent) {
    const meta = e.metaKey || e.ctrlKey;
    if (meta && e.key.toLowerCase() === "k") {
      e.preventDefault();
      this.search.toggle();
      return;
    }
    if (e.key === "Escape" && this.search.isOpen()) {
      this.search.close();
    }
  }

  go(path: string[]) {
    this.search.close();
    this.router.navigate(path);
  }

  goContact() {
    this.go(["/contact"]);
  }
}
